import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User, Mail, MapPin, Calendar, Globe, ChefHat, Coffee, Utensils,
  Clock, Wine, Flame, AlertCircle, Save, ArrowLeft, Loader, Camera, Trash2
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useUser } from '../contexts/UserContext';
import { useLanguage } from '../contexts/LanguageContext';
import LocationSearch from '../components/LocationSearch';
import BackButton from '../components/BackButton';
import { 
  cuisineOptions, 
  dietaryOptions, 
  priceRangeOptions, 
  languageOptions, 
  interestOptions 
} from '../data/filterOptions';

const ProfileSettings = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { t } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deletingProfile, setDeletingProfile] = useState(false);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    dateOfBirth: '',
    location: '',
    location_lat: null as number | null,
    location_lng: null as number | null,
    bio: '',
    favoriteCuisines: [] as string[],
    dietaryPreferences: [] as string[],
    priceRange: '',
    preferredDiningTimes: [] as string[],
    languages: [] as string[],
    interests: [] as string[],
    diningStyle: '',
    drinkingPreference: '',
    smokingPreference: '',
    privacySettings: {
      profile_visibility: 'public',
      show_age: true,
      show_location: true,
      show_interests: true,
      show_languages: true,
      show_smoking_preference: true,
      show_dietary_preferences: true,
      show_drinking_preference: true
    }
  });

  const diningTimeOptions = [
    'Früher Morgen (06:00-08:00)', 'Frühstück (08:00-10:00)',
    'Brunch (10:00-12:00)', 'Mittagessen (12:00-14:00)',
    'Nachmittag (14:00-17:00)', 'Frühes Abendessen (17:00-19:00)',
    'Abendessen (19:00-21:00)', 'Später Abend (21:00+)'
  ];

  const diningStyleOptions = [
    { value: 'casual', label: 'Casual' },
    { value: 'fine_dining', label: 'Fine Dining' },
    { value: 'social', label: 'Sozial' },
    { value: 'adventurous', label: 'Erlebnisdinner' }
  ];

  const drinkingPreferenceOptions = [
    { value: 'non_drinker', label: 'Nicht-Trinker' },
    { value: 'social_drinker', label: 'Gesellschaftstrinker' },
    { value: 'wine_enthusiast', label: 'Wein Liebhaber' },
    { value: 'cocktail_lover', label: 'Cocktail Enthusiast' },
    { value: 'beer_enthusiast', label: 'Bier Liebhaber' }
  ];

  const smokingPreferenceOptions = [
    { value: 'non_smoker', label: 'Nichtraucher' },
    { value: 'social_smoker', label: 'Party Raucher' },
    { value: 'regular_smoker', label: 'Raucher' },
    { value: 'vaper', label: 'Vaper' }
  ];

  const privacyLabels: Record<string, string> = {
    show_age: 'Alter anzeigen',
    show_location: 'Standort anzeigen',
    show_languages: 'Sprachen anzeigen',
    show_interests: 'Interessen anzeigen',
    show_dietary_preferences: 'Essgewohnheiten anzeigen',
    show_drinking_preference: 'Trinkverhalten anzeigen',
    show_smoking_preference: 'Rauchverhalten anzeigen',
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    fetchUserProfile();
  }, [user, navigate]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user?.id)
        .single();

      if (profileError) throw profileError;

      // Format date of birth if it exists
      let formattedDateOfBirth = '';
      if (profile.date_of_birth) {
        const date = new Date(profile.date_of_birth);
        formattedDateOfBirth = date.toISOString().split('T')[0];
      }

      setFormData({
        fullName: profile.full_name || '',
        email: profile.email || '',
        dateOfBirth: formattedDateOfBirth,
        location: profile.location || '',
        location_lat: profile.location_lat,
        location_lng: profile.location_lng,
        bio: profile.bio || '',
        favoriteCuisines: profile.favorite_cuisines || [],
        dietaryPreferences: profile.dietary_preferences || [],
        priceRange: profile.price_range || '',
        preferredDiningTimes: profile.preferred_dining_times || [],
        languages: profile.languages || [],
        interests: profile.interests || [],
        diningStyle: profile.dining_style || '',
        drinkingPreference: profile.drinking_preference || '',
        smokingPreference: profile.smoking_preference || '',
        privacySettings: profile.privacy_settings || {
          profile_visibility: 'public',
          show_age: true,
          show_location: true,
          show_interests: true,
          show_languages: true,
          show_smoking_preference: true,
          show_dietary_preferences: true,
          show_drinking_preference: true
        }
      });

      // Set profile picture preview if it exists
      if (profile.profile_picture) {
        setProfilePicturePreview(profile.profile_picture);
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLocationSelect = (location: { address: string; lat: number; lng: number }) => {
    setFormData(prev => ({
      ...prev,
      location: location.address,
      location_lat: location.lat,
      location_lng: location.lng
    }));
  };

  const handleCheckboxChange = (name: string, value: string) => {
    setFormData(prev => {
      const currentValues = prev[name as keyof typeof prev] as string[];
      return {
        ...prev,
        [name]: currentValues.includes(value)
          ? currentValues.filter(item => item !== value)
          : [...currentValues, value]
      };
    });
  };

  const handleProfileVisibilityChange = (visibility: string) => {
    setFormData(prev => ({
      ...prev,
      privacySettings: { ...prev.privacySettings, profile_visibility: visibility }
    }));
  };

  const handlePrivacyChange = (key: string) => {
    setFormData(prev => ({
      ...prev,
      privacySettings: {
        ...prev.privacySettings,
        [key]: !prev.privacySettings[key as keyof typeof prev.privacySettings]
      }
    }));
  };

  const handleProfilePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    
    const file = e.target.files[0];
    if (file.size > 2 * 1024 * 1024) {
      setError('Profilbilder müssen unter 2MB sein');
      return;
    }
    
    // Create a preview
    setProfilePicturePreview(URL.createObjectURL(file));
    
    try {
      setUploadingPicture(true);
      setError(null);
      
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/profile.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(fileName, file, { upsert: true });
        
      if (uploadError) throw uploadError;
      
      const { data: { publicUrl } } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(fileName);
        
      // Update profile with new picture URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ profile_picture: publicUrl })
        .eq('id', user.id);
        
      if (updateError) throw updateError;
      
      setSuccess('Profile picture updated successfully');
    } catch (err) {
      console.error('Error uploading profile picture:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload profile picture');
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      navigate('/login');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      // Prepare the update data
      const updateData = {
        full_name: formData.fullName,
        email: formData.email,
        date_of_birth: formData.dateOfBirth || null,
        location: formData.location,
        location_lat: formData.location_lat,
        location_lng: formData.location_lng,
        bio: formData.bio,
        favorite_cuisines: formData.favoriteCuisines,
        dietary_preferences: formData.dietaryPreferences,
        price_range: formData.priceRange,
        preferred_dining_times: formData.preferredDiningTimes,
        languages: formData.languages,
        interests: formData.interests,
        dining_style: formData.diningStyle,
        drinking_preference: formData.drinkingPreference,
        smoking_preference: formData.smokingPreference,
        privacy_settings: formData.privacySettings,
        updated_at: new Date()
      };
      
      const { error: updateError } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id);
        
      if (updateError) throw updateError;
      
      setSuccess('Profile updated successfully');
      
      // Scroll to top to show success message
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to update profile');
      
      // Scroll to top to show error message
      window.scrollTo(0, 0);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      setDeletingProfile(true);
      setError(null);

      // First delete the profile
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', user.id);

      if (profileError) throw profileError;

      // Then delete the user account
      const { error: authError } = await supabase.auth.admin.deleteUser(user.id);
      if (authError) throw authError;

      // Sign out the user
      await supabase.auth.signOut();

      // Redirect to home page
      navigate('/');
    } catch (err) {
      console.error('Error deleting profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete profile');
      setShowDeleteConfirmation(false);
    } finally {
      setDeletingProfile(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center space-x-4 mb-8">
        <BackButton />
        <h1 className="text-2xl font-bold text-gray-900">Profileinstellungen</h1>
      </div>

      {error && (
        <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded flex items-center">
          <Save className="h-5 w-5 mr-2" />
          <span>{success}</span>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Profile Picture */}
          <div className="flex flex-col items-center space-y-4">
            <div className="relative w-32 h-32">
              {profilePicturePreview ? (
                <img
                  src={profilePicturePreview}
                  alt="Profile preview"
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center">
                  <User className="h-12 w-12 text-gray-400" />
                </div>
              )}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPicture}
                className="absolute bottom-0 right-0 bg-orange-500 text-white p-2 rounded-full hover:bg-orange-600 transition"
              >
                {uploadingPicture ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <Camera className="h-4 w-4" />
                )}
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleProfilePictureChange}
              className="hidden"
            />
            <span className="text-sm text-gray-500">
              Profilbild hochladen (max 2MB)
            </span>
          </div>

          {/* Basic Information */}
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">Grundinformationen</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4" />
                    <span>Ganzer Name</span>
                  </div>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4" />
                    <span>E-Mail</span>
                  </div>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md"
                  disabled
                />
                <p className="text-xs text-gray-500 mt-1">E-Mail kann nicht geändert werden</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4" />
                    <span>Geburtsdatum</span>
                  </div>
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center space-x-2">
                    <MapPin className="h-4 w-4" />
                    <span>Wohnort</span>
                  </div>
                </label>
                <LocationSearch
                  onLocationSelect={handleLocationSelect}
                  defaultValue={formData.location}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                rows={3}
                className="w-full p-2 border rounded-md"
                placeholder="Erzähle uns über dich..."
              />
            </div>
          </div>

          {/* Dining Preferences */}
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">Essensvorlieben</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-2">
                  <ChefHat className="h-4 w-4" />
                  <span>Lieblingsessen</span>
                </div>
              </label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {cuisineOptions.map((cuisine) => (
                  <label key={cuisine} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.favoriteCuisines.includes(cuisine)}
                      onChange={() => handleCheckboxChange('favoriteCuisines', cuisine)}
                      className="rounded text-orange-500 focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-600">{cuisine}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-2">
                  <Utensils className="h-4 w-4" />
                  <span>Ernährungswünsche</span>
                </div>
              </label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {dietaryOptions.map((option) => (
                  <label key={option} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.dietaryPreferences.includes(option)}
                      onChange={() => handleCheckboxChange('dietaryPreferences', option)}
                      className="rounded text-orange-500 focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-600">{option}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4" />
                  <span>Bevorzugte Zeit zum Essen</span>
                </div>
              </label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {diningTimeOptions.map((time) => (
                  <label key={time} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.preferredDiningTimes.includes(time)}
                      onChange={() => handleCheckboxChange('preferredDiningTimes', time)}
                      className="rounded text-orange-500 focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-600">{time}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Preisklasse</label>
              <select
                name="priceRange"
                value={formData.priceRange}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
              >
                <option value="">Preisklasse wählen</option>
                {priceRangeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Additional Information */}
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">Zusätzliche Informationen</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-2">
                  <Wine className="h-4 w-4" />
                  <span>Trinkgewohnheiten</span>
                </div>
              </label>
              <select
                name="drinkingPreference"
                value={formData.drinkingPreference}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
              >
                <option value="">Trinkgewohnheiten wählen</option>
                {drinkingPreferenceOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-2">
                  <Flame className="h-4 w-4" />
                  <span>Raucher</span>
                </div>
              </label>
              <select
                name="smokingPreference"
                value={formData.smokingPreference}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
              >
                <option value="">Option wählen</option>
                {smokingPreferenceOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-2">
                  <Globe className="h-4 w-4" />
                  <span>Gesprochene Sprachen</span>
                </div>
              </label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {languageOptions.map((language) => (
                  <label key={language} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.languages.includes(language)}
                      onChange={() => handleCheckboxChange('languages', language)}
                      className="rounded text-orange-500 focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-600">{language}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-2">
                  <Coffee className="h-4 w-4" />
                  <span>Besondere Interessen</span>
                </div>
              </label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {interestOptions.map((interest) => (
                  <label key={interest} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.interests.includes(interest)}
                      onChange={() => handleCheckboxChange('interests', interest)}
                      className="rounded text-orange-500 focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-600">{interest}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stil der Dinner</label>
              <select
                name="diningStyle"
                value={formData.diningStyle}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
              >
                <option value="">Wähle Stil</option>
                {diningStyleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Privacy Settings */}
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">Privatsphäre Einstellungen</h2>
            
            {/* Profile Visibility */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Profil Sichtbarkeit
              </label>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    checked={formData.privacySettings.profile_visibility === 'public'}
                    onChange={() => handleProfileVisibilityChange('public')}
                    className="text-orange-500 focus:ring-orange-500"
                  />
                  <span>Öffentlich - Jeder kann das Profil sehen</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    checked={formData.privacySettings.profile_visibility === 'friends'}
                    onChange={() => handleProfileVisibilityChange('friends')}
                    className="text-orange-500 focus:ring-orange-500"
                  />
                  <span>Nur Freunde - Nur angenommene Freunde können das Profil sehen</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    checked={formData.privacySettings.profile_visibility === 'private'}
                    onChange={() => handleProfileVisibilityChange('private')}
                    className="text-orange-500 focus:ring-orange-500"
                  />
                  <span>Privat - Nur du kannst das Profil sehen</span>
                </label>
              </div>
            </div>

            {/* Information Visibility */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Informations Sichtbarkeit
              </label>
              <div className="space-y-2">
                {Object.entries(formData.privacySettings)
                  .filter(([key]) => key !== 'profile_visibility')
                  .map(([key, value]) => (
                    <label key={key} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={value as boolean}
                        onChange={() => handlePrivacyChange(key)}
                        className="rounded text-orange-500 focus:ring-orange-500"
                      />
                      <span>{privacyLabels[key] ?? key}</span>
                    </label>
                  ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/profile')}
              className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-50 transition"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition flex items-center space-x-2"
            >
              {saving ? (
                <>
                  <Loader className="h-5 w-5 animate-spin" />
                  <span>Speichern...</span>
                </>
              ) : (
                <>
                  <Save className="h-5 w-5" />
                  <span>Änderungen speichern</span>
                </>
              )}
            </button>
          </div>
        </form>

        {/* Delete Profile Section */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <h2 className="text-xl font-semibold text-red-600 mb-4">Profil löschen</h2>
          <p className="text-gray-600 mb-4">
            Wenn du dein Profil löschst, werden alle deine Daten unwiderruflich gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.
          </p>
          <button
            type="button"
            onClick={() => setShowDeleteConfirmation(true)}
            className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition flex items-center space-x-2"
          >
            <Trash2 className="h-5 w-5" />
            <span>Profil löschen</span>
          </button>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Bist du sicher?</h3>
              <p className="text-gray-600 mb-6">
                Diese Aktion wird dein Profil und alle zugehörigen Daten unwiderruflich löschen. Dies kann nicht rückgängig gemacht werden.
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirmation(false)}
                  className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-50 transition"
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={handleDeleteProfile}
                  disabled={deletingProfile}
                  className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition flex items-center space-x-2"
                >
                  {deletingProfile ? (
                    <>
                      <Loader className="h-5 w-5 animate-spin" />
                      <span>Löschen...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-5 w-5" />
                      <span>Endgültig löschen</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileSettings;